import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchCommunityStats, getCommunityLastSeenAt } from './api';

export function useCommunityStats({ refreshMs = 120_000, enabled = true } = {}) {
  const { currentUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!currentUser || !enabled) return;
    setLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const data = await fetchCommunityStats({ token });
      setStats(data);
    } catch (e) {
      // silenzioso: è solo UI sugar
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [currentUser, enabled]);

  useEffect(() => {
    if (!enabled) return;
    load();
    if (!refreshMs) return;
    const id = window.setInterval(() => {
      load();
    }, refreshMs);
    return () => window.clearInterval(id);
  }, [load, refreshMs, enabled]);

  const hasNew = useMemo(() => {
    if (!stats) return false;
    const lastSeen = getCommunityLastSeenAt();
    const latest = String(stats.latest_created_at || '').trim();
    if (!latest) return false;

    const latestTs = Date.parse(latest);
    if (!Number.isFinite(latestTs)) return false;

    if (!lastSeen) {
      return Number(stats.recent_count_24h || 0) > 0;
    }

    const seenTs = Date.parse(String(lastSeen));
    if (!Number.isFinite(seenTs)) return Number(stats.recent_count_24h || 0) > 0;

    return latestTs > seenTs;
  }, [stats]);

  return {
    stats,
    loading,
    hasNew,
    refresh: load,
  };
}
